import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Radio, Users, Lock, Volume2 } from "lucide-react";
import { api, createWS } from "../api";
import { useAuth } from "../AuthContext";
import { usePTT } from "../hooks/usePTT";
import PTTButton from "../components/PTTButton";
import PresenceDot from "../components/PresenceDot";
import CreateChannelModal from "../components/CreateChannelModal";

export default function ChannelsPage() {
  const { user } = useAuth();
  const [channels, setChannels]       = useState([]);
  const [active, setActive]           = useState(null);      // current channel obj
  const [messages, setMessages]       = useState([]);
  const [presence, setPresence]       = useState([]);        // online users in active channel
  const [chatInput, setChatInput]     = useState("");
  const [showCreate, setShowCreate]   = useState(false);
  const [loading, setLoading]         = useState(true);
  const [wsState, setWsState]         = useState("disconnected"); // connected | disconnected
  const wsRef = useRef(null);
  const msgEndRef = useRef(null);

  const token = user?.token || "";

  // Load channels
  const loadChannels = useCallback(async () => {
    try {
      const list = await api.getChannels();
      setChannels(list);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadChannels(); }, [loadChannels]);

  // Auto-scroll messages
  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // Connect WebSocket when active channel changes
  useEffect(() => {
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    if (!active || !token) { setWsState("disconnected"); setPresence([]); return; }

    const ws = createWS(active.channel_id, token, {
      onOpen:  ()    => setWsState("connected"),
      onClose: ()    => setWsState("disconnected"),
      onError: ()    => setWsState("disconnected"),
      onEvent: (evt) => {
        handleEvent(evt);
        if (evt.type === "presence") setPresence(evt.online || []);
      },
      onAudio: (buf) => playAudio(buf),
    });
    wsRef.current = ws;

    // Load message history
    api.getMessages(active.channel_id).then(setMessages).catch(() => {});

    return () => { ws.close(); };
    // eslint-disable-next-line
  }, [active?.channel_id, token]);

  const { transmitting, speakingUser, startPTT, stopPTT, playAudio, handleEvent } =
    usePTT(wsRef.current);

  const joinChannel = async (ch) => {
    if (active?.channel_id === ch.channel_id) return;
    if (active) await api.leaveChannel(active.channel_id).catch(() => {});
    setActive(ch);
    await api.joinChannel(ch.channel_id).catch(() => {});
    // reload channels to update member_count
    loadChannels();
  };

  const sendChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || !active) return;
    const text = chatInput.trim();
    setChatInput("");
    try {
      const msg = await api.sendMessage(active.channel_id, text);
      setMessages((m) => [...m, msg]);
    } catch {}
  };

  const onChannelCreated = (ch) => {
    setChannels((prev) => [ch, ...prev]);
    setShowCreate(false);
    joinChannel(ch);
  };

  return (
    <div className="flex flex-1 h-screen overflow-hidden">
      {/* Channel List */}
      <div className="w-72 bg-gray-900 border-r border-gray-800 flex flex-col">
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <h2 className="font-semibold text-white">Channels</h2>
          {(user.role === "admin" || user.role === "employer") && (
            <button onClick={() => setShowCreate(true)}
              className="p-1.5 rounded-lg bg-brand text-white hover:bg-brand-dark">
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {loading ? (
            <p className="text-gray-500 text-sm px-3 py-4">Loading…</p>
          ) : channels.length === 0 ? (
            <p className="text-gray-500 text-sm px-3 py-4">No channels yet.</p>
          ) : channels.map((ch) => (
            <button
              key={ch.channel_id}
              onClick={() => joinChannel(ch)}
              className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors group ${
                active?.channel_id === ch.channel_id
                  ? "bg-brand/20 border border-brand/40"
                  : "hover:bg-gray-800"
              }`}
            >
              <div className="flex items-center gap-2">
                {ch.is_private ? <Lock className="w-3.5 h-3.5 text-gray-500" /> : <Radio className="w-3.5 h-3.5 text-gray-500" />}
                <span className="text-sm font-medium text-white truncate flex-1">{ch.name}</span>
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Users className="w-3 h-3" />{ch.member_count || 0}
                </span>
              </div>
              {ch.description && (
                <p className="text-xs text-gray-500 mt-0.5 truncate pl-5">{ch.description}</p>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Main Area */}
      <div className="flex-1 flex flex-col">
        {active ? (
          <>
            {/* Channel Header */}
            <div className="px-6 py-3 border-b border-gray-800 bg-gray-950 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Radio className="w-5 h-5 text-brand" />
                <div>
                  <h1 className="font-semibold text-white">{active.name}</h1>
                  {active.description && <p className="text-xs text-gray-400">{active.description}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                  wsState === "connected" ? "bg-green-500/20 text-green-400" : "bg-gray-700 text-gray-400"
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${wsState === "connected" ? "bg-green-400" : "bg-gray-500"}`} />
                  {wsState === "connected" ? "Live" : "Offline"}
                </span>
              </div>
            </div>

            <div className="flex flex-1 overflow-hidden">
              {/* Chat + PTT */}
              <div className="flex-1 flex flex-col">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {messages.map((m) => (
                    <div key={m.msg_id} className="flex gap-2">
                      <div className="w-7 h-7 rounded-full bg-brand/40 flex items-center justify-center text-xs text-white font-bold flex-shrink-0">
                        {(m.sender_name || m.sender_id || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs font-semibold text-white">{m.sender_name || m.sender_id}</span>
                          <span className="text-xs text-gray-500">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                        <p className="text-sm text-gray-200">{m.text}</p>
                      </div>
                    </div>
                  ))}
                  {speakingUser && (
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <Volume2 className="w-4 h-4 animate-pulse" />
                      {speakingUser} is speaking…
                    </div>
                  )}
                  <div ref={msgEndRef} />
                </div>
                {/* Chat input */}
                <form onSubmit={sendChat} className="p-4 border-t border-gray-800 flex gap-2">
                  <input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder={`Message #${active.name}`}
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand"
                  />
                  <button type="submit"
                    className="px-4 py-2 bg-brand text-white rounded-lg text-sm hover:bg-brand-dark">
                    Send
                  </button>
                </form>
              </div>

              {/* PTT + Presence Panel */}
              <div className="w-56 border-l border-gray-800 bg-gray-900 flex flex-col items-center gap-6 p-6">
                <PTTButton
                  transmitting={transmitting}
                  onStart={startPTT}
                  onStop={stopPTT}
                  disabled={wsState !== "connected"}
                />
                <div className="w-full">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Online</p>
                  {presence.length === 0 ? (
                    <p className="text-xs text-gray-600">Only you</p>
                  ) : (
                    <div className="space-y-1.5">
                      {presence.map((u) => (
                        <div key={u.user_id} className="flex items-center gap-2">
                          <PresenceDot online />
                          <span className="text-xs text-gray-300 truncate">{u.name || u.user_id}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-center">
            <div>
              <Radio className="w-14 h-14 text-gray-700 mx-auto mb-4" />
              <p className="text-gray-400 font-medium">Select a channel to start</p>
              <p className="text-gray-600 text-sm mt-1">Or create one above</p>
            </div>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateChannelModal onClose={() => setShowCreate(false)} onCreated={onChannelCreated} />
      )}
    </div>
  );
}
